import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Calendar, ChevronLeft, ChevronRight, Info, CheckCircle2, XCircle, Minus, Inbox, AlertTriangle } from 'lucide-react';
import { Button } from 'components/ui/button';
import EmptyState from 'components/ui/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { Card, CardContent } from 'components/ui/card';
import Switch from 'components/ui/switch';
import { RenderedHtml, toPlainText } from 'modules/editor';
import * as ResultModule from 'modules/result';
import {
    AnswerReviewBody,
    AnswerReviewStyles,
    hasAnswer,
    type AnswerReviewItem,
} from 'components/result/AnswerReviewBody';
import { useTranslation } from 'react-i18next';

type Respondent = ResultModule.Types.IEntity.Result;
type DetailAnswer = ResultModule.Types.IEntity.ResultDetails['answers'][number];

// Backend caps `perPage` at 100 (ListResultsDto @Max); we fetch each loaded
// respondent's detail to aggregate answers by question — the view is
// question-centric (groups every respondent's answer to the currently
// selected question), not row-centric, so there is no single "expanded row"
// to lazily gate a query behind. A deeper roster would need a dedicated
// by-question API; until then we cap concurrency (see
// RESPONDENT_DETAIL_BATCH_SIZE) instead of firing up to 100 parallel
// requests on mount.
const RESPONDENT_PAGE_SIZE = 100;

// How many respondent-detail requests may be in flight at once. Queries
// beyond this window stay `enabled: false` until the active window settles,
// then the window advances — bounding backend load without changing what
// data ultimately loads.
const RESPONDENT_DETAIL_BATCH_SIZE = 8;

/** For compact dropdown labels where rendered math wouldn't fit; the question body uses RenderedHtml. */
const htmlToPlain = toPlainText;

const pad2 = (n: number) => String(n).padStart(2, '0');

const fmtDay = (d: Date | null): string => {
    if (!d) return '';
    const date = new Date(d);
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

interface QuestionResponse {
    respondentId: string;
    respondentName: string;
    day: string;
    answer: DetailAnswer;
}

interface QuestionGroup {
    questionId: string;
    questionText: string;
    isOpenEnded: boolean;
    responses: QuestionResponse[];
}

/** Derived from the answer shape, so it doesn't depend on the exact question-type string set. */
const isOpenEndedAnswer = (ans: DetailAnswer): boolean => !ans.options && !ans.matchPairs;

export const AnswersReviewPage: React.FC = () => {
    const { t } = useTranslation();
    const { testId } = useParams<{ testId: string }>();
    const [dateFilter, setDateFilter] = useState('');
    const [selectedQuestionId, setSelectedQuestionId] = useState('');
    const [openEndedOnly, setOpenEndedOnly] = useState(false);

    const { items, isLoading: listLoading, isError: listError } = ResultModule.Hooks.useList({
        params: { testId: testId || undefined, perPage: RESPONDENT_PAGE_SIZE },
        enabled: Boolean(testId),
    });
    const respondents = items as Respondent[];

    // How many respondents' details are allowed to fetch so far. Starts at one
    // batch and advances once the active batch settles (see effect below), so
    // at most RESPONDENT_DETAIL_BATCH_SIZE requests are in flight at a time
    // instead of fanning out to the whole roster on mount.
    const [detailBatchCursor, setDetailBatchCursor] = useState(RESPONDENT_DETAIL_BATCH_SIZE);

    // Reset the batch window whenever the roster changes test (route param
    // changes without an unmount, e.g. navigating between two tests' review
    // pages), so a stale, larger cursor doesn't re-enable every query at once.
    useEffect(() => {
        setDetailBatchCursor(RESPONDENT_DETAIL_BATCH_SIZE);
    }, [testId]);

    // Fetch each respondent's full answer detail; useQueries keeps the hook order
    // stable as the roster grows and dedupes via the shared ['result','single']
    // cache (so switching from Sheets Review reuses already-loaded attempts).
    // Only the current batch window is `enabled` — see detailBatchCursor.
    const detailQueries = useQueries({
        queries: respondents.map((r, idx) => {
            const [queryKey, queryFn] = ResultModule.Queries.Single({ id: r.id });
            return { queryKey, queryFn, enabled: Boolean(r.id) && idx < detailBatchCursor };
        }),
    });
    const detailsLoading = detailQueries.some((q) => q.isLoading);

    // Advance the batch window once every query inside it has settled
    // (succeeded or errored) so the next respondents start fetching.
    const activeWindowSize = Math.min(detailBatchCursor, respondents.length);
    const activeWindowSettledCount = detailQueries.reduce(
        (count, q, idx) => (idx < detailBatchCursor && !q.isLoading ? count + 1 : count),
        0,
    );
    useEffect(() => {
        if (detailBatchCursor >= respondents.length) return;
        if (activeWindowSettledCount >= activeWindowSize) {
            setDetailBatchCursor((prev) => Math.min(prev + RESPONDENT_DETAIL_BATCH_SIZE, respondents.length));
        }
    }, [activeWindowSettledCount, activeWindowSize, detailBatchCursor, respondents.length]);

    // Distinct completion days for the date filter.
    const dayOptions = Array.from(
        new Set(respondents.map((r) => fmtDay(r.completedAt)).filter(Boolean)),
    ).sort((a, b) => b.localeCompare(a));

    // Aggregate answers by question across respondents. The question list is
    // built from the attempt details themselves (not the test structure), so it
    // stays correct even when questions are materialized per attempt.
    const groups: QuestionGroup[] = [];
    const groupIndex = new Map<string, QuestionGroup>();
    detailQueries.forEach((query, idx) => {
        const detail = query.data?.item;
        const respondent = respondents[idx];
        if (!detail?.id || !respondent) return;
        const day = fmtDay(respondent.completedAt);
        if (dateFilter && day !== dateFilter) return;
        const respondentName = detail.user?.name || respondent.userName || '—';
        (detail.answers || []).forEach((answer) => {
            if (!answer.questionId) return;
            let group = groupIndex.get(answer.questionId);
            if (!group) {
                group = {
                    questionId: answer.questionId,
                    questionText: answer.questionText,
                    isOpenEnded: isOpenEndedAnswer(answer),
                    responses: [],
                };
                groupIndex.set(answer.questionId, group);
                groups.push(group);
            }
            group.responses.push({ respondentId: detail.id, respondentName, day, answer });
        });
    });

    const visibleGroups = openEndedOnly ? groups.filter((g) => g.isOpenEnded) : groups;
    const activeId =
        selectedQuestionId && visibleGroups.some((g) => g.questionId === selectedQuestionId)
            ? selectedQuestionId
            : visibleGroups[0]?.questionId ?? '';
    const activeIndex = visibleGroups.findIndex((g) => g.questionId === activeId);
    const current = activeIndex >= 0 ? visibleGroups[activeIndex] : undefined;

    if (listLoading) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-foreground">{t('answers_review')}</h2>
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{t('dates')}</span>
                        <Select
                            value={dateFilter || '__all__'}
                            onValueChange={(v) => setDateFilter(v === '__all__' ? '' : v)}
                        >
                            <SelectTrigger className="w-36">
                                <SelectValue placeholder={t('all_dates')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">{t('all_dates')}</SelectItem>
                                {dayOptions.map((day) => (
                                    <SelectItem key={day} value={day}>
                                        {day}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {visibleGroups.length > 0 && (
                        <div className="flex items-center gap-2">
                            <Select value={activeId} onValueChange={setSelectedQuestionId}>
                                <SelectTrigger className="min-w-[200px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {visibleGroups.map((g, i) => {
                                        const plain = htmlToPlain(g.questionText);
                                        return (
                                            <SelectItem key={g.questionId} value={g.questionId}>
                                                {t('q')} {i + 1} {plain.slice(0, 30)}
                                                {plain.length > 30 ? '...' : ''}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                disabled={activeIndex <= 0}
                                onClick={() =>
                                    setSelectedQuestionId(visibleGroups[activeIndex - 1].questionId)
                                }
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                disabled={activeIndex < 0 || activeIndex >= visibleGroups.length - 1}
                                onClick={() =>
                                    setSelectedQuestionId(visibleGroups[activeIndex + 1].questionId)
                                }
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-muted-foreground whitespace-nowrap">
                            {t('browse_open_ended_questions_only')}
                        </label>
                        <Switch checked={openEndedOnly} onChange={setOpenEndedOnly} />
                    </div>
                </div>
            </div>

            {listError ? (
                <Card>
                    <EmptyState icon={<AlertTriangle size={32} />} title={t('failed_to_load_results')} />
                </Card>
            ) : respondents.length === 0 ? (
                <Card>
                    <EmptyState icon={<Inbox size={32} />} title={t('no_completed_attempts')} />
                </Card>
            ) : visibleGroups.length === 0 && detailsLoading ? (
                <div className="flex items-center justify-center min-h-[200px]">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
                </div>
            ) : current ? (
                <div className="ts-shell space-y-4">
                    <Card>
                        <CardContent className="p-6 space-y-2">
                            <div className="text-sm font-medium text-foreground">
                                <span className="mr-1">
                                    {t('q')} {activeIndex + 1}
                                </span>
                                <RenderedHtml html={current.questionText || ''} />
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {current.responses.length} {t('respondents')}
                            </p>
                        </CardContent>
                    </Card>

                    {current.responses.length === 0 ? (
                        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3 flex gap-2 items-start">
                            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                                {t('there_are_no_answers_to_this_question_yet')}
                            </p>
                        </div>
                    ) : (
                        current.responses.map((resp, i) => {
                            const answered = hasAnswer(resp.answer.answer);
                            return (
                                <Card key={`${resp.respondentId}-${i}`}>
                                    <CardContent className="p-4 space-y-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {resp.respondentName}
                                                </p>
                                                {resp.day && (
                                                    <p className="text-xs text-muted-foreground">
                                                        {resp.day}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-xs font-medium text-muted-foreground tabular-nums">
                                                    {resp.answer.pointsEarned}/{resp.answer.maxPoints}P
                                                </span>
                                                {!answered ? (
                                                    <Minus className="h-4 w-4 text-muted-foreground" />
                                                ) : resp.answer.isCorrect ? (
                                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                )}
                                            </div>
                                        </div>
                                        <AnswerReviewBody answer={resp.answer as AnswerReviewItem} />
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                    <AnswerReviewStyles />
                </div>
            ) : (
                <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                        {t('there_are_no_answers_to_this_question_yet')}
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default AnswersReviewPage;
